/**
 * Shade Selector (PoC)
 *
 * Drives the shade dropdown + swatches rendered by snippets/shade-selector.liquid.
 * Selecting a shade fetches the whole product-main section for the target product
 * through the Section Rendering API and swaps it in place — no page reload. The
 * fresh <react-product-gallery> and <buy-box-form-v3> custom elements self-mount
 * on connectedCallback, so gallery images, price, USPs, accordions and the CTA
 * color all update from the server-rendered HTML.
 *
 * Any other section on the page that depends on the active shade opts in with a
 * `data-shade-refresh` attribute (e.g. sections/before-after.liquid reading the
 * product metafield). Those sections ride along in the same Section Rendering
 * API request and are swapped with the same HTML, so their custom elements
 * remount with the new product's data.
 *
 * All listeners are delegated at document level so they survive innerHTML swaps.
 * Any failure falls back to full navigation to the target product URL.
 */

(function () {
  'use strict';

  if (window.__shadeSelectorInit) return;
  window.__shadeSelectorInit = true;

  var OPEN_CLASS = 'shade-selector__dropdown--open';
  // Section Rendering API accepts at most 5 section ids per request; one is main.
  var MAX_EXTRA_SECTIONS = 4;
  var abortController = null;

  /* ---- lookups ---- */

  function getSelectorRoot() {
    return document.querySelector('[data-shade-selector]');
  }

  // Resolve the Shopify section wrapper that gets swapped. Its id doubles as the
  // Section Rendering API section id (template--{id}__main), which is why every
  // member of a shade group must share the same product template.
  function getSectionWrapper(root) {
    var el = root || getSelectorRoot();
    if (el) {
      var wrapper = el.closest('.shopify-section');
      if (wrapper && wrapper.id.indexOf('shopify-section-') === 0) return wrapper;
    }
    var main = document.querySelector('[data-section-name="product-main"]');
    if (main && main.dataset.sectionId) {
      return document.getElementById('shopify-section-' + main.dataset.sectionId);
    }
    return null;
  }

  function getSectionId(wrapper) {
    return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
  }

  // Sections flagged with [data-shade-refresh] get re-rendered for the new
  // shade alongside product-main. Same template constraint applies: their ids
  // are template-specific, so a null render for one of them is simply skipped.
  function getRefreshWrappers(mainWrapper) {
    var wrappers = [];
    document.querySelectorAll('[data-shade-refresh]').forEach(function (el) {
      var wrapper = el.closest('.shopify-section');
      if (!wrapper || wrapper === mainWrapper) return;
      if (wrapper.id.indexOf('shopify-section-') !== 0) return;
      if (wrappers.indexOf(wrapper) === -1) wrappers.push(wrapper);
    });
    return wrappers.slice(0, MAX_EXTRA_SECTIONS);
  }

  /* ---- dropdown open/close ---- */

  function closeDropdowns() {
    var open = document.querySelectorAll(
      '[data-shade-selector] [data-shade-dropdown].' + OPEN_CLASS
    );
    open.forEach(function (dropdown) {
      dropdown.classList.remove(OPEN_CLASS);
      var trigger = dropdown.querySelector('[data-shade-dropdown-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function toggleDropdown(trigger) {
    var dropdown = trigger.closest('[data-shade-dropdown]');
    if (!dropdown) return;
    var isOpen = dropdown.classList.contains(OPEN_CLASS);
    closeDropdowns();
    if (!isOpen) {
      dropdown.classList.add(OPEN_CLASS);
      trigger.setAttribute('aria-expanded', 'true');
    }
  }

  /* ---- optimistic UI while the section fetch is in flight ---- */

  function applyActiveState(root, handle) {
    if (!root) return;
    root.dataset.currentHandle = handle;

    var activeLabel = '';
    var activeTag = '';

    root.querySelectorAll('[data-shade-swatch]').forEach(function (swatch) {
      var isActive = swatch.dataset.shadeHandle === handle;
      swatch.classList.toggle('shade-selector__swatch--active', isActive);
      swatch.setAttribute('aria-pressed', String(isActive));
      if (isActive) {
        activeLabel = swatch.dataset.shadeLabel || '';
        activeTag = swatch.dataset.shadeTag || '';
      }
    });

    root.querySelectorAll('[data-shade-option]').forEach(function (option) {
      var isActive = option.dataset.shadeHandle === handle;
      option.classList.toggle('shade-selector__option--active', isActive);
      option.setAttribute('aria-selected', String(isActive));
      if (isActive) {
        activeLabel = option.dataset.shadeLabel || activeLabel;
        activeTag = option.dataset.shadeTag || activeTag;
      }
    });

    var triggerLabel = root.querySelector('[data-shade-dropdown-label]');
    if (triggerLabel && activeLabel) triggerLabel.textContent = activeLabel;

    var triggerTag = root.querySelector('[data-shade-dropdown-tag]');
    if (triggerTag) {
      triggerTag.textContent = activeTag;
      triggerTag.hidden = !activeTag;
    }
  }

  /* ---- section swap ---- */

  // Ported from buy-box-smp-ajax.js: stylesheets inside the swapped HTML get
  // hoisted to <head> (deduped by href) so styles never flash/reset on swap.
  function ensureStylesheetInHead(href) {
    if (!href) return;
    var links = document.head.querySelectorAll('link[rel="stylesheet"]');
    for (var i = 0; i < links.length; i++) {
      if ((links[i].getAttribute('href') || '') === href) return;
    }
    var link = document.createElement('link');
    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('href', href);
    document.head.appendChild(link);
  }

  // Parse a Section Rendering API payload into a DOM node ready to be injected:
  // stylesheets hoisted to <head>, external scripts dropped.
  function prepareSource(sectionId, html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var source = doc.getElementById('shopify-section-' + sectionId) || doc.body;

    source.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
      ensureStylesheetInHead(link.getAttribute('href'));
      link.remove();
    });

    // Injected <script src> tags never execute; the bundles are already loaded.
    source.querySelectorAll('script[src]').forEach(function (el) {
      el.remove();
    });

    return source;
  }

  // Generic swap for opt-in sections: their custom elements (e.g.
  // <before-after-component>) disconnect/reconnect and rebuild themselves.
  function swapExtraSection(wrapper, sectionId, html) {
    var source = prepareSource(sectionId, html);
    wrapper.innerHTML = source.innerHTML;
  }

  function swapSection(wrapper, sectionId, html) {
    var source = prepareSource(sectionId, html);

    var previousPurchaseType = 'subscription';
    var checked = wrapper.querySelector('[data-purchase-type]:checked');
    if (checked) previousPurchaseType = checked.value;

    // Atomic replacement: disconnects the old <react-product-gallery> and
    // <buy-box-form-v3> and connects fresh ones, which self-init and re-fetch
    // their own data (the gallery keys off the new handle in its js-data).
    wrapper.innerHTML = source.innerHTML;

    requestAnimationFrame(function () {
      // Belt and braces: init() no-ops when connectedCallback already ran.
      var formEl = wrapper.querySelector('buy-box-form-v3');
      if (formEl && typeof formEl.init === 'function') formEl.init();

      if (previousPurchaseType === 'one-time') {
        var oneTimeHeader = wrapper.querySelector('[data-plan-type="one-time"] [data-plan-header]');
        if (oneTimeHeader) oneTimeHeader.click();
      }
    });
  }

  function updateDocumentTitle(oldTitleText, wrapper) {
    var h1 = wrapper.querySelector('.product-details-v2__title');
    if (!h1) return;
    var newText = h1.textContent.trim();
    if (!newText) return;
    if (oldTitleText && document.title.indexOf(oldTitleText) !== -1) {
      document.title = document.title.replace(oldTitleText, newText);
    } else {
      document.title = newText;
    }
  }

  /* ---- selection flow ---- */

  function selectShade(handle, url, options) {
    options = options || {};
    var root = getSelectorRoot();
    var wrapper = getSectionWrapper(root);
    var sectionId = getSectionId(wrapper);

    // No swappable section found — plain navigation always works.
    if (!wrapper || !sectionId) {
      window.location.assign(url);
      return;
    }

    var oldTitleEl = wrapper.querySelector('.product-details-v2__title');
    var oldTitleText = oldTitleEl ? oldTitleEl.textContent.trim() : '';

    var extraWrappers = getRefreshWrappers(wrapper);
    var sectionIds = [sectionId].concat(extraWrappers.map(getSectionId));

    applyActiveState(root, handle);
    wrapper.setAttribute('aria-busy', 'true');
    extraWrappers.forEach(function (extra) {
      extra.setAttribute('aria-busy', 'true');
    });

    if (abortController) abortController.abort();
    abortController = new AbortController();

    fetch('/products/' + handle + '?sections=' + encodeURIComponent(sectionIds.join(',')), {
      signal: abortController.signal
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var html = data[sectionId];
        // Null/missing section = the target product uses a different template.
        if (!html) throw new Error('Section "' + sectionId + '" not rendered for ' + handle);

        swapSection(wrapper, sectionId, html);

        extraWrappers.forEach(function (extra) {
          var extraId = getSectionId(extra);
          var extraHtml = data[extraId];
          if (extraHtml) swapExtraSection(extra, extraId, extraHtml);
          extra.removeAttribute('aria-busy');
        });

        if (options.push !== false) {
          history.pushState({ shadeSwap: true, handle: handle }, '', url);
        }
        updateDocumentTitle(oldTitleText, wrapper);

        document.dispatchEvent(
          new CustomEvent('shade:change', { bubbles: true, detail: { handle: handle, url: url } })
        );

        wrapper.removeAttribute('aria-busy');
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return; // superseded by a newer click
        console.error('[ShadeSelector]', err);
        extraWrappers.forEach(function (extra) {
          extra.removeAttribute('aria-busy');
        });
        window.location.assign(url);
      });
  }

  /* ---- delegated listeners (bound once, survive swaps) ---- */

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-shade-dropdown-trigger]');
    if (trigger) {
      toggleDropdown(trigger);
      return;
    }

    var option = e.target.closest('[data-shade-swatch], [data-shade-option]');
    if (option) {
      closeDropdowns();
      var root = option.closest('[data-shade-selector]');
      var handle = option.dataset.shadeHandle;
      var url = option.dataset.shadeUrl;
      if (!root || !handle || !url) return;
      if (handle === root.dataset.currentHandle) return;
      selectShade(handle, url);
      return;
    }

    if (!e.target.closest('[data-shade-dropdown]')) closeDropdowns();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDropdowns();
  });

  // Back/forward within the shade set swaps via AJAX; anything else reloads.
  window.addEventListener('popstate', function () {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    var handle = match ? decodeURIComponent(match[1]) : null;
    if (!handle) {
      window.location.reload();
      return;
    }

    var root = getSelectorRoot();
    if (root && handle === root.dataset.currentHandle) return;

    var target = root
      ? root.querySelector('[data-shade-swatch][data-shade-handle="' + CSS.escape(handle) + '"]')
      : null;

    if (target) {
      selectShade(handle, target.dataset.shadeUrl, { push: false });
    } else {
      window.location.reload();
    }
  });
})();
